import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEqual, isNil, isUndefined } from 'lodash-es';
import styled from 'styled-components';

import PageLoader from '@/components/page-loader';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationLink, authenticationLoginEmail, updateUser } from '@/store/slices/authentication';
import { sessionEnsure } from '@/store/slices/session';
import { isEmailValid } from '@/utils/strings';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveLoginRedirect } from '@/utils/login-redirect';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';

interface LoginProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: Record<string, string>;
}

const ModuleElementDiv = styled.div<{ $styleText: string }>(
  (props) => `
    ${props.$styleText};

    /* The app sets \`color-scheme: dark\` globally (src/index.scss) so browser-painted
       surfaces stay dark. That also flips the UA's default form-control text color to
       white, which turns the config-styled white email field white-on-white. Force the
       light UA scheme on the text inputs so their default text is dark again; any
       explicit color a tenant's config sets still wins. Checkboxes/radios are left on
       the dark scheme. */
    & input:not([type='checkbox']):not([type='radio']) {
      color-scheme: light;
    }
  `,
);

const LoginModule: React.FC<LoginProps> = (componentProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication, isEqual);
  const casinoInstance = useAppSelector((state) => state.casinoInstance, isEqual);
  const session = useAppSelector((state) => state.session, isEqual);

  const [searchParams, setSearchParams] = useSearchParams();
  const [layout, setLayout] = React.useState<'form' | 'next'>('form');
  const [email, setEmail] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState<boolean>(false);

  // Get magic link
  const magicLink = React.useMemo<string | null>(() => {
    return searchParams.get('link');
  }, [searchParams]);

  // Where the auth service aimed THIS link (`redirect_url` on the redemption response),
  // resolved to an in-app path. Kept in a ref so it cannot itself re-trigger the routing
  // effect below and overwrite the destination with the default route.
  const redirectTargetRef = React.useRef<string | null>(null);

  // Magic link request
  React.useEffect(() => {
    if (!isNil(magicLink)) {
      setLoading(true);

      // Unwrapped only to read `redirect_url` off the response; a rejection is still handled
      // by the slice + the "magic link error" effect below, so it is swallowed here.
      dispatch(authenticationLink({ link: magicLink }))
        .unwrap()
        .then((result) => {
          redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
        })
        .catch(() => undefined);
    }
  }, [dispatch, magicLink]);

  // Magic link success
  React.useEffect(() => {
    const authCompleted = !authentication.in_progress && authentication.auth_type === 'user';
    const casinoCompleted = !casinoInstance.loading && casinoInstance.loaded;

    if (!isNil(magicLink) && authCompleted && casinoCompleted && !isNil(session.sessionId)) {
      setLoading(true);

      // A `redirect_url` on the redemption response wins over the brand-state routing: the
      // link was minted for a destination, and that intent outranks what we would otherwise
      // infer from the session.
      navigate(
        redirectTargetRef.current ??
          resolvePostAuthRoute({
            sessionStatus: session.status,
            brandsLoaded: casinoInstance.loaded,
            brandsCount: casinoInstance.brands.length,
            configuratorBase: resolveConfiguratorFlowBase(),
          }),
        // Replace the `?link=` entry so Back can't put the credential URL in front of the tag manager.
        { replace: true },
      );
    }
  }, [authentication, casinoInstance, session, navigate, magicLink]);

  // Magic link error
  React.useEffect(() => {
    if (!isNil(magicLink) && !authentication.in_progress && !isNil(authentication.error)) {
      setLoading(false);

      searchParams.delete('link');

      setSearchParams(searchParams, { replace: true });
    }
  }, [authentication, searchParams, setSearchParams, magicLink]);

  // Init session: with no magic link and no known session, resume an existing build
  // or create one (sign-in funnel).
  React.useEffect(() => {
    if (isNil(magicLink) && isNil(session.sessionId)) {
      dispatch(sessionEnsure());
    }
  }, [dispatch, session, magicLink]);

  // Post authentication handler
  React.useEffect(() => {
    const nextPage = resolvePostAuthRoute({
      sessionStatus: session.status,
      brandsLoaded: casinoInstance.loaded,
      brandsCount: casinoInstance.brands.length,
      configuratorBase: resolveConfiguratorFlowBase(),
    });

    switch (session.postAuth) {
      case 'mail':
        setLayout('next');
        break;

      case 'redirect':
        setLoading(true);
        navigate(nextPage);
        break;

      default:
        break;
    }
  }, [
    navigate,
    session.postAuth,
    session.status,
    casinoInstance.brands.length,
    casinoInstance.loaded,
    casinoInstance.loading,
  ]);

  // Email input change handler
  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setEmail(event.target.value);
  }, []);

  // Form validation
  const isValid = React.useMemo<boolean>(() => {
    return isEmailValid(email);
  }, [email]);

  // Form submitting clicking button
  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || isNil(session.sessionId) || !isValid) {
        return;
      }

      dispatch(updateUser({ email }));

      dispatch(authenticationLoginEmail({ email, sessionId: session.sessionId }));
    },
    [dispatch, session, email, isValid],
  );

  // Form submitting pressing enter key
  const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLFormElement>>(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        handleSubmit(event);
      }
    },
    [handleSubmit],
  );

  // Final context
  const contextValue = React.useMemo(
    () => ({
      layout,
      email,
      isValid,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onSubmit: handleSubmit,
    }),
    [layout, email, isValid, handleChange, handleKeyDown, handleSubmit],
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ModuleElementDiv className={componentProps.className} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default LoginModule;
